import type { UseTransferContactSearchReturn } from './index.types';
import { ContactModelV2 } from 'Models/ContactModelV2';
import { useInfiniteContacts } from 'Modules/contacts/requests';
import type { TransferSearchResult } from 'Modules/softphone/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MIN_DIRECT_NUMBER_LENGTH,
  SEARCH_DEBOUNCE_MS,
  TRANSFER_CONTACTS_LIMIT,
} from './index.constants';
import {
  checkIsPhoneNumber,
  getDialableLength,
  mapContactToTransferResult,
  matchesSearchQuery,
} from './utils';

/**
 * Hook for searching contacts in the transfer context.
 *
 * Debounces the query by 300ms before executing the contact search API call,
 * maps results to TransferSearchResult format, and detects phone number patterns
 * to expose a direct-dial option.
 *
 * @param query - The search query string
 */
export const useTransferContactSearch = (query: string): UseTransferContactSearchReturn => {
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce the query by 300ms
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(query);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  const {
    data: contactsData,
    isLoading: isQueryLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteContacts(debouncedQuery, TRANSFER_CONTACTS_LIMIT);

  const isDebouncing = query.trim() !== debouncedQuery.trim();
  const isLoading = isDebouncing || isQueryLoading;

  const trimmedQuery = query.trim();
  const isPhoneNumberQuery = checkIsPhoneNumber(trimmedQuery);

  // Show direct number option when query looks like a phone number and has
  // at least MIN_DIRECT_NUMBER_LENGTH dialable characters (so formatting
  // alone, e.g. "(7)", does not qualify).
  const showDirectNumberOption =
    isPhoneNumberQuery && getDialableLength(trimmedQuery) >= MIN_DIRECT_NUMBER_LENGTH;

  const results: TransferSearchResult[] = useMemo(() => {
    const contacts = contactsData?.pages?.flatMap(
      (page) => page.contacts as ContactModelV2[]
    ) ?? [];

    const mapped = contacts.map(mapContactToTransferResult);

    // Match the legacy `TransferDirectory` behaviour: drop contacts that
    // have no dialable destination (no phone numbers and no extension).
    // The mapper returns an empty `phoneNumbers` list for these cases.
    const withDestinations = mapped.filter(
      (result) => result.phoneNumbers.length > 0
    );

    if (!trimmedQuery) {
      return withDestinations;
    }

    return withDestinations.filter((result) => matchesSearchQuery(result, trimmedQuery));
  }, [contactsData, trimmedQuery]);

  const handleFetchNextPage = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    results,
    isLoading,
    showDirectNumberOption,
    isPhoneNumberQuery,
    fetchNextPage: handleFetchNextPage,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
  };
};
